<template>
  <div class="availability-manager-container mt-3">
    <div class="dc-card p-3 p-md-4">
      <!-- Main Header -->
      <!-- Main Header -->
      <div class="header-bar d-flex align-items-center justify-content-between flex-wrap pb-3 mb-3 border-bottom">
        <div class="d-flex align-items-center mb-2 mb-md-0 header-title-group">
          <div class="dc-icon-box mr-3">
            <i class="fa fa-calendar-check-o text-white"></i>
          </div>
          <div>
            <h4 class="dc-card-title mb-0">Manage Calendly Meeting Timings</h4>
            <p class="dc-card-desc mb-0">
              Live availability schedules synchronized directly with your Calendly account.
            </p>
          </div>
        </div>
        <div class="d-flex align-items-center flex-wrap header-actions-group">
          <div class="timezone-badge d-flex align-items-center mr-2 mb-1 mb-sm-0">
            <i class="fa fa-globe text-primary mr-1"></i>
            <span class="small font-weight-bold text-muted mr-1">Timezone:</span>
            <span class="badge badge-light border text-dark font-weight-bold">{{ timezone }}</span>
          </div>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm refresh-btn mb-1 mb-sm-0"
            @click="loadDynamicCalendlyData"
            :disabled="isLoading"
            title="Fetch latest dynamic slots from Calendly"
          >
            <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': isLoading }"></i>
            Sync Dynamic Slots
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <i class="fa fa-circle-o-notch fa-spin fa-2x text-primary mb-2"></i>
        <p class="text-muted small">Fetching dynamic schedule details directly from Calendly API...</p>
      </div>

      <!-- Scope / Meeting Selector Bar -->
      <div v-else class="meeting-scope-selector mb-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
          <label class="scope-label mb-0 font-weight-bold text-dark">
            <i class="fa fa-sliders text-primary mr-1"></i> Select Meeting to Manage:
          </label>
          <div class="scope-badge text-muted small d-none d-sm-block">
            <span class="text-dark font-weight-bold">
              Editing: {{ currentMeeting ? currentMeeting.name : '' }}
            </span>
          </div>
        </div>

        <!-- Meeting Tabs (Responsive 2-column grid on mobile, inline on desktop) -->
        <div class="meeting-tabs">
          <button
            v-for="m in meetings"
            :key="m.id"
            type="button"
            class="meeting-tab-btn"
            :class="{ active: activeTab === m.id }"
            @click="setTab(m.id)"
          >
            <i class="fa fa-calendar-o mr-1"></i>
            {{ m.name }}
            <span class="tab-duration">({{ m.duration }})</span>
          </button>
        </div>
      </div>

      <!-- Quick Actions Bar -->
      <div v-if="!isLoading" class="presets-bar d-flex align-items-center justify-content-between flex-wrap p-2 mb-3 rounded bg-light border">
        <div class="small font-weight-bold text-dark mr-2 mb-1 mb-sm-0">
          <i class="fa fa-sliders text-secondary mr-1"></i> Quick Action:
        </div>
        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm preset-btn"
            @click="clearAllDays"
            title="Clear all days"
          >
            <i class="fa fa-ban mr-1 text-danger"></i> Clear All Days
          </button>
        </div>
      </div>

      <!-- Days of the Week List (Monday - Sunday) -->
      <div v-if="!isLoading" class="days-list">
        <div
          v-for="day in activeDays"
          :key="day.key"
          class="day-row p-3 mb-2 rounded"
          :class="{ 'day-inactive': !day.active }"
        >
          <div class="row align-items-center">
            <!-- Day Name & Active Toggle -->
            <div class="col-12 col-md-3 mb-2 mb-md-0 d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <label class="dc-switch mr-3 mb-0" :for="'toggle-' + day.key">
                  <input
                    type="checkbox"
                    :id="'toggle-' + day.key"
                    v-model="day.active"
                    @change="onDayToggle(day)"
                    aria-label="Toggle active day"
                  />
                  <span class="dc-slider round"></span>
                </label>
                <div>
                  <span class="font-weight-bold day-name d-block" :class="{ 'text-muted': !day.active }">
                    {{ day.name }}
                  </span>
                  <span v-if="day.active" class="extra-small text-muted">
                    {{ daySummary(day) }}
                  </span>
                </div>
              </div>

              <!-- Mobile Status Pill -->
              <span class="badge d-md-none" :class="day.active ? 'badge-success' : 'badge-light'">
                {{ day.active ? 'Active' : 'Unavailable' }}
              </span>
            </div>

            <!-- Time Blocks or Unavailable State -->
            <div class="col-12 col-md-9">
              <div v-if="!day.active" class="text-muted small font-italic py-2 d-flex align-items-center">
                <i class="fa fa-ban mr-2 text-secondary"></i> Unavailable all day
                <button
                  type="button"
                  class="btn btn-link btn-sm text-primary p-0 ml-3 small"
                  @click="activateDay(day)"
                >
                  <i class="fa fa-plus-circle mr-1"></i> Make Available
                </button>
              </div>

              <div v-else class="time-blocks-wrap">
                <div
                  v-for="(block, bIndex) in day.timeBlocks"
                  :key="bIndex"
                  class="time-block-row mb-2"
                >
                  <div class="time-slot-line d-flex align-items-center">
                    <div class="d-flex align-items-center flex-grow-1 time-inputs-pair">
                      <input
                        type="time"
                        class="form-control dc-time-input"
                        v-model="block.start"
                        aria-label="Start time"
                      />
                      <span class="mx-1 mx-sm-2 text-muted font-weight-bold">-</span>
                      <input
                        type="time"
                        class="form-control dc-time-input"
                        v-model="block.end"
                        aria-label="End time"
                      />
                    </div>

                    <!-- Remove Block Button -->
                    <button
                      type="button"
                      class="btn btn-outline-danger btn-sm dc-touch-btn remove-block-btn ml-2"
                      @click="removeTimeBlock(day, bIndex)"
                      title="Remove time slot"
                      aria-label="Remove time slot"
                    >
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </div>

                  <!-- Copy Day's Schedule to Weekdays Button -->
                  <button
                    v-if="bIndex === 0 && day.timeBlocks.length > 0"
                    type="button"
                    class="btn btn-light border btn-sm copy-day-btn mt-2"
                    @click="copyDayToWeekdays(day)"
                    title="Copy these hours to Monday through Friday"
                  >
                    <i class="fa fa-clone mr-1"></i> Apply to Mon-Fri
                  </button>
                </div>

                <!-- Add Time Block Button -->
                <button
                  type="button"
                  class="btn btn-link btn-sm text-primary p-0 add-block-btn mt-1"
                  @click="addTimeBlock(day)"
                >
                  <i class="fa fa-plus-circle mr-1"></i> Add Another Time Slot
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sticky / Floating Save Changes Bar -->
      <div v-if="!isLoading" class="sticky-save-bar mt-4 pt-3 border-top d-flex align-items-center justify-content-between flex-wrap">
        <div class="small text-muted mb-2 mb-sm-0">
          <i class="fa fa-info-circle text-primary mr-1"></i>
          Editing: <strong class="text-dark">{{ currentMeeting ? currentMeeting.name : '' }}</strong>
        </div>

        <div class="d-flex align-items-center">
          <button
            type="button"
            class="btn dc-save-btn"
            @click="initiateSave"
            :disabled="isLoading || isSaving"
          >
            <span v-if="isSaving">
              <i class="fa fa-spinner fa-spin mr-1"></i> Saving to Calendly...
            </span>
            <span v-else>
              <i class="fa fa-check mr-2"></i> Save Changes
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CalendlyAPI from "@/api/calendly";

function createEmptyDays() {
  return [
    { key: "monday", name: "Monday", active: false, timeBlocks: [] },
    { key: "tuesday", name: "Tuesday", active: false, timeBlocks: [] },
    { key: "wednesday", name: "Wednesday", active: false, timeBlocks: [] },
    { key: "thursday", name: "Thursday", active: false, timeBlocks: [] },
    { key: "friday", name: "Friday", active: false, timeBlocks: [] },
    { key: "saturday", name: "Saturday", active: false, timeBlocks: [] },
    { key: "sunday", name: "Sunday", active: false, timeBlocks: [] },
  ];
}

function cloneDays(daysList) {
  return JSON.parse(JSON.stringify(daysList));
}

export default {
  name: "AvailabilityManager",
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isConnected: false,
      activeTab: "onboarding-1",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
      targetMeetingsForSave: [],

      // Initialized without dummy slot timings
      meetings: [
        {
          id: "onboarding-1",
          shortName: "Onboarding #1",
          name: "DC Trainings - Onboarding Meeting #1",
          duration: "30 min",
          type: "One-on-One",
          scheduleUuid: "sched-onboarding-1",
          days: createEmptyDays(),
        },
        {
          id: "onboarding-2",
          shortName: "Onboarding #2",
          name: "DC Trainings - Onboarding Meeting #2",
          duration: "30 min",
          type: "One-on-One",
          scheduleUuid: "sched-onboarding-2",
          days: createEmptyDays(),
        },
        {
          id: "onboarding-3",
          shortName: "Onboarding #3",
          name: "DC Trainings - Onboarding Meeting #3",
          duration: "30 min",
          type: "One-on-One",
          scheduleUuid: "sched-onboarding-3",
          days: createEmptyDays(),
        },
        {
          id: "onboarding-4",
          shortName: "Onboarding #4",
          name: "DC Trainings - Onboarding Meeting #4",
          duration: "30 min",
          type: "One-on-One",
          scheduleUuid: "sched-onboarding-4",
          days: createEmptyDays(),
        },
        {
          id: "coaching-15min",
          shortName: "Coaching Call",
          name: "DC Trainings Coaching - 15 Minutes",
          duration: "15 min",
          type: "One-on-One",
          scheduleUuid: "sched-coaching-15min",
          days: createEmptyDays(),
        },
      ],
    };
  },

  computed: {
    currentMeeting() {
      return this.meetings.find((m) => m.id === this.activeTab) || this.meetings[0];
    },
    activeDays() {
      return this.currentMeeting ? this.currentMeeting.days : [];
    },
  },

  async mounted() {
    await this.loadDynamicCalendlyData();
  },

  methods: {
    async loadDynamicCalendlyData() {
      const currentToken = CalendlyAPI.getToken();
      if (!currentToken || currentToken.startsWith("dummy_")) {
        this.isConnected = false;
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      try {
        // 1. Get current user
        const user = await CalendlyAPI.getCurrentUser();
        if (!user || !user.uri) {
          this.isConnected = false;
          this.isLoading = false;
          return;
        }

        this.isConnected = true;
        if (user.timezone) {
          this.timezone = user.timezone;
        }

        // 2. Fetch live event types dynamically
        const eventTypes = await CalendlyAPI.fetchEventTypes(user.uri);
        if (eventTypes && eventTypes.length > 0) {
          // Sort meetings so Onboarding #1 through #4 appear first, followed by Coaching
          const sortedEvents = [...eventTypes].sort((a, b) => {
            const getScore = (name) => {
              if (name.includes("#1")) return 1;
              if (name.includes("#2")) return 2;
              if (name.includes("#3")) return 3;
              if (name.includes("#4")) return 4;
              return 5;
            };
            return getScore(a.name) - getScore(b.name);
          });

          // Fetch each event type's specific schedule in parallel
          const dynamicMeetings = await Promise.all(
            sortedEvents.map(async (et, index) => {
              let cleanShortName = et.name
                .replace(/DC Trainings\s*-\s*/i, "")
                .replace(/DC Trainings Coaching\s*-\s*/i, "Coaching ")
                .replace(/Meeting\s*/i, "")
                .trim();

              const meetingDays = createEmptyDays();
              let meetingScheduleUuid = `sched-${index}`;

              // Fetch exact event-level availability schedule
              const eventSchedule = await CalendlyAPI.fetchEventTypeSchedule(et.uri);
              if (eventSchedule) {
                if (eventSchedule.uri) {
                  meetingScheduleUuid = eventSchedule.uri.split("/").pop();
                }
                if (eventSchedule.timezone) {
                  this.timezone = eventSchedule.timezone;
                }
                if (eventSchedule.rules && Array.isArray(eventSchedule.rules)) {
                  eventSchedule.rules.forEach((rule) => {
                    if (!rule.wday) return;
                    const targetDay = meetingDays.find(
                      (d) => d.key.toLowerCase() === rule.wday.toLowerCase()
                    );
                    if (targetDay && rule.intervals && rule.intervals.length > 0) {
                      targetDay.active = true;
                      targetDay.timeBlocks = rule.intervals.map((iv) => ({
                        start: iv.from,
                        end: iv.to,
                      }));
                    }
                  });
                }
              }

              return {
                id: et.slug || `event-${index}`,
                shortName: cleanShortName,
                name: et.name,
                uri: et.uri,
                duration: `${et.duration || 30} min`,
                type: et.kind || "One-on-One",
                scheduleUuid: meetingScheduleUuid,
                days: meetingDays,
              };
            })
          );

          this.meetings = dynamicMeetings;
          if (this.meetings[0]) {
            this.activeTab = this.meetings[0].id;
          }
        }
      } catch (err) {
        console.warn("[Calendly] Error loading dynamic data:", err.message);
        this.isConnected = false;
      } finally {
        this.isLoading = false;
      }
    },

    setTab(tabId) {
      this.activeTab = tabId;
    },

    activateDay(day) {
      day.active = true;
      if (!day.timeBlocks || day.timeBlocks.length === 0) {
        day.timeBlocks = [{ start: "09:00", end: "17:00" }];
      }
    },

    addTimeBlock(day) {
      if (!day.timeBlocks) day.timeBlocks = [];
      day.timeBlocks.push({ start: "09:00", end: "17:00" });
    },

    removeTimeBlock(day, index) {
      day.timeBlocks.splice(index, 1);
      if (day.timeBlocks.length === 0) {
        day.active = false;
      }
    },

    copyDayToWeekdays(sourceDay) {
      const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
      this.activeDays.forEach((day) => {
        if (weekdays.includes(day.key)) {
          day.active = sourceDay.active;
          day.timeBlocks = JSON.parse(JSON.stringify(sourceDay.timeBlocks));
        }
      });
      if (this.$swal) {
        this.$swal({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
          type: "success",
          title: `Copied ${sourceDay.name} hours to Mon–Fri!`,
        });
      }
    },

    daySummary(day) {
      if (!day.active || !day.timeBlocks || day.timeBlocks.length === 0) {
        return "Unavailable";
      }
      return day.timeBlocks
        .map((tb) => `${this.formatTime(tb.start)} - ${this.formatTime(tb.end)}`)
        .join(", ");
    },

    formatTime(timeStr) {
      if (!timeStr) return "";
      const [h, m] = timeStr.split(":");
      const hour = parseInt(h, 10);
      const ampm = hour >= 12 ? "pm" : "am";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m} ${ampm}`;
    },

    clearAllDays() {
      this.activeDays.forEach((day) => {
        day.active = false;
        day.timeBlocks = [];
      });
      this.showPresetToast("Cleared all availability");
    },

    showPresetToast(msg) {
      if (this.$swal) {
        this.$swal({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
          type: "info",
          title: msg,
        });
      }
    },

    onDayToggle(day) {
      if (day.active && (!day.timeBlocks || day.timeBlocks.length === 0)) {
        day.timeBlocks = [{ start: "09:00", end: "17:00" }];
      }
    },

    validateTimeSlots() {
      const m = this.currentMeeting;
      if (!m || !m.days) return true;

      for (const day of m.days) {
        if (day.active) {
          if (!day.timeBlocks || day.timeBlocks.length === 0) {
            if (this.$swal) {
              this.$swal({
                type: "warning",
                title: "Missing Time Slot",
                text: `${day.name} is enabled, but has no time slots. Please add a time slot or turn the day off.`,
              });
            }
            return false;
          }
          for (const tb of day.timeBlocks) {
            if (!tb.start || !tb.end) {
              if (this.$swal) {
                this.$swal({
                  type: "warning",
                  title: "Incomplete Time Slot",
                  text: `Please enter both start and end time for ${day.name}.`,
                });
              }
              return false;
            }
            if (tb.start >= tb.end) {
              if (this.$swal) {
                this.$swal({
                  type: "warning",
                  title: "Invalid Time Slot",
                  text: `On ${day.name}: Start time (${this.formatTime(tb.start)}) must be earlier than end time (${this.formatTime(tb.end)}).`,
                });
              }
              return false;
            }
          }
        }
      }
      return true;
    },

    initiateSave() {
      if (!this.validateTimeSlots()) return;

      this.targetMeetingsForSave = [this.currentMeeting];
      const targetLabel = `"${this.currentMeeting.name}"`;

      if (this.$swal) {
        this.$swal({
          title: "Save Availability to Calendly?",
          text: `Are you sure you want to update live availability for ${targetLabel}?`,
          type: "question",
          showCancelButton: true,
          confirmButtonColor: "#305397",
          cancelButtonColor: "#6c757d",
          confirmButtonText: "Yes, Save Changes",
          cancelButtonText: "Cancel",
        }).then(async (result) => {
          if (result && (result.value || result === true)) {
            await this.executePatchRequest();
          }
        });
      } else {
        this.executePatchRequest();
      }
    },

    async executePatchRequest() {
      this.isSaving = true;
      try {
        const updates = this.targetMeetingsForSave.map((m) => ({
          meetingId: m.id,
          meetingName: m.name,
          eventTypeUri: m.uri,
          rules: CalendlyAPI.formatUiDaysToRules(m.days),
          timezone: this.timezone,
        }));

        console.log("[Calendly] Dispatching schedule updates:", updates);
        const result = await CalendlyAPI.updateMultipleSchedules(updates);
        console.log("[Calendly] Update result:", result);

        if (!result.success) {
          const failed = result.results.find((r) => !r.success);
          const errDetail =
            failed && failed.error && failed.error.message
              ? failed.error.message
              : "Failed to update schedule with Calendly.";
          throw new Error(errDetail);
        }

        // Re-sync dynamic schedule from Calendly to guarantee verified state
        await this.loadDynamicCalendlyData();

        if (this.$swal) {
          const count = this.targetMeetingsForSave.length;
          this.$swal({
            type: "success",
            title: "Availability Successfully Saved!",
            text: `Calendly live availability has been updated for ${count} meeting(s).`,
          });
        }
      } catch (error) {
        console.error("[Calendly] Error updating schedules:", error);
        if (this.$swal) {
          this.$swal({
            type: "error",
            title: "Update Failed",
            text: error.message || "Failed to update schedule with Calendly.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.availability-manager-container {
  font-family: "Calibri", "Segoe UI", Arial, sans-serif;
  color: #343434;
}

.dc-card {
  background-color: #fff;
  border: 1px solid #e6e8ea;
  border-top: 3px solid #305397;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.dc-card-title {
  font-size: 19px;
  font-weight: 600;
  color: #305397;
}

.dc-card-desc {
  font-size: 14px;
  color: #666;
}

.dc-icon-box {
  width: 44px;
  height: 44px;
  min-width: 44px;
  background-color: #305397;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.refresh-btn {
  border-radius: 5px !important;
  font-size: 13px;
  height: 34px;
}

.dc-retry-btn {
  border-radius: 4px !important;
  font-size: 12px;
}

/* Meeting Scope Selector */
.meeting-scope-selector {
  background-color: #f8fafc;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.scope-label {
  font-size: 14px;
}

.meeting-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meeting-tab-btn {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 8px 14px;
  border-radius: 5px !important;
  font-size: 13.5px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.meeting-tab-btn:hover {
  background-color: #f1f5f9;
  border-color: #94a3b8;
  color: #1e293b;
}

.meeting-tab-btn.active {
  background-color: #305397;
  border-color: #305397;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(48, 83, 151, 0.25);
}

.tab-duration {
  font-size: 12px;
  opacity: 0.85;
  margin-left: 4px;
}

/* Presets Bar */
.presets-bar {
  gap: 8px;
}

.preset-btn {
  border-radius: 5px !important;
  font-size: 12.5px;
  padding: 4px 10px;
}

/* Day Row Styling */
.day-row {
  background-color: #ffffff;
  border: 1px solid #eaedf1;
  transition: all 0.2s ease;
}

.day-row:hover {
  border-color: #cbd5e1;
}

.day-inactive {
  background-color: #f8fafc;
  opacity: 0.85;
}

.day-name {
  font-size: 15px;
  min-width: 90px;
}

.extra-small {
  font-size: 11.5px;
}

/* Touch-friendly Switch */
.dc-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  min-height: 26px;
}

.dc-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.dc-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.3s;
  border-radius: 34px;
}

.dc-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

input:checked + .dc-slider {
  background-color: #305397;
}

input:checked + .dc-slider:before {
  transform: translateX(22px);
}

/* Time Inputs */
.dc-time-input {
  width: 120px;
  height: 40px;
  font-size: 14px;
  border-radius: 5px;
  border: 1px solid #ced4da;
  text-align: center;
}

.remove-block-btn {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px !important;
}

.copy-day-btn {
  height: 40px;
  border-radius: 5px !important;
  font-size: 12px;
  color: #475569;
}

.add-block-btn {
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}

.add-block-btn:hover {
  color: #25427a;
  text-decoration: underline;
}

/* Save Button */
.dc-save-btn {
  background-color: #305397 !important;
  border: 1px solid #305397 !important;
  color: #ffffff !important;
  height: 44px;
  padding: 0 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 5px !important;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(48, 83, 151, 0.2);
}

.dc-save-btn:hover {
  background-color: #25427a !important;
}

.dc-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.code-preview {
  max-height: 240px;
  overflow-y: auto;
  border-radius: 6px;
}

@media (max-width: 768px) {
  .dc-card {
    padding: 14px !important;
  }

  .dc-card-title {
    font-size: 17px;
  }

  .dc-card-desc {
    font-size: 13px;
  }

  .dc-icon-box {
    width: 38px;
    height: 38px;
    min-width: 38px;
    font-size: 17px;
  }

  .header-actions-group {
    width: 100%;
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
  }

  .meeting-scope-selector {
    padding: 12px;
  }

  /* Clean 2-column grid for meeting cards on mobile */
  .meeting-tabs {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 8px !important;
  }

  .meeting-tab-btn {
    width: 100% !important;
    padding: 9px 6px !important;
    font-size: 12.5px !important;
    text-align: center !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    min-height: 44px !important;
  }

  /* Make odd 5th item take full width of row */
  .meeting-tab-btn:last-child:nth-child(odd) {
    grid-column: span 2 !important;
    flex-direction: row !important;
  }

  .presets-bar {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .presets-bar > div:last-child {
    width: 100%;
    display: flex;
    gap: 6px;
    margin-top: 6px;
  }

  .preset-btn {
    flex: 1;
    text-align: center;
    padding: 6px 6px;
    font-size: 12px;
  }

  .day-row {
    padding: 12px !important;
  }

  .time-slot-line {
    width: 100%;
  }

  .time-inputs-pair {
    width: auto;
  }

  .dc-time-input {
    flex: 1;
    min-width: 90px;
    height: 42px;
    font-size: 14px;
    padding: 2px 6px;
  }

  .remove-block-btn {
    width: 42px;
    height: 42px;
    min-width: 42px;
  }

  .copy-day-btn {
    width: 100%;
    height: 38px;
    margin-top: 6px;
  }

  .sticky-save-bar {
    position: sticky;
    bottom: 0;
    background: #ffffff;
    padding: 12px 14px;
    z-index: 1000;
    box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.1);
    margin-left: -14px;
    margin-right: -14px;
    border-radius: 0 0 6px 6px;
    flex-direction: column;
    align-items: stretch !important;
  }

  .sticky-save-bar > div {
    margin-bottom: 10px;
  }

  .dc-save-btn {
    width: 100%;
    height: 48px;
    font-size: 15.5px;
  }
}

@media (max-width: 576px) {
  .meeting-tabs {
    /* On narrow phones, users can pick from the select dropdown or swipe pills */
    padding-bottom: 8px;
  }

  .presets-bar > div:last-child {
    flex-direction: column;
  }

  .preset-btn {
    width: 100%;
  }
}
</style>
